(() => {
  'use strict';

  const initialized = new WeakSet();
  const QUERY_PARAMETER = 'csc_q';
  const PAGE_SIZE = 250;
  const MAX_PAGES = 40;
  const MAX_CONCURRENT_REQUESTS = 4;
  const REQUEST_TIMEOUT_MS = 15000;
  const MAX_RENDERED_RESULTS = 250;
  const MAX_TITLE_LENGTH = 300;
  const MAX_HANDLE_LENGTH = 255;
  const MAX_IMAGE_URL_LENGTH = 2048;

  const normalize = (value) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase();

  const selectProductRecord = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const prices = variants
      .map((variant) => Number.parseFloat(variant?.price))
      .filter(Number.isFinite);
    const image = Array.isArray(product?.images) ? product.images[0] : null;

    return {
      id: String(product?.id || '').slice(0, 64),
      title: typeof product?.title === 'string'
        ? product.title.slice(0, MAX_TITLE_LENGTH)
        : '',
      handle: typeof product?.handle === 'string'
        ? product.handle.slice(0, MAX_HANDLE_LENGTH)
        : '',
      minimumPrice: prices.length ? Math.min(...prices) : null,
      maximumPrice: prices.length ? Math.max(...prices) : null,
      hasVariants: variants.length > 0,
      available: variants.some((variant) => variant?.available === true),
      image: {
        src: typeof image?.src === 'string'
          ? image.src.slice(0, MAX_IMAGE_URL_LENGTH)
          : '',
        width: Number.parseInt(image?.width, 10),
        height: Number.parseInt(image?.height, 10),
      },
    };
  };

  const initializeAll = (container = document) => {
    if (!container?.querySelectorAll) return;
    container.querySelectorAll('[data-csc-collection-search]').forEach(initialize);
  };

  const initialize = (root) => {
    if (initialized.has(root)) return;
    initialized.add(root);

    const form = root.querySelector('[data-csc-collection-search-form]');
    const input = form?.elements.namedItem(QUERY_PARAMETER);
    const clearButton = root.querySelector('[data-csc-collection-search-clear]');
    const status = root.querySelector('[data-csc-collection-search-status]');
    const loading = root.querySelector('[data-csc-collection-search-loading]');
    const results = root.querySelector('[data-csc-collection-search-results]');
    const scope = root.closest('.csc-collection') || document;
    const browseSelector = root.dataset.cscCollectionBrowseSelector || '';
    const browse = browseSelector ? scope.querySelector(browseSelector) : null;
    const collectionUrl = String(root.dataset.cscCollectionUrl || '').replace(/\/$/, '');
    const collectionProductCount = Number.parseInt(
      root.dataset.cscCollectionProductCount || '0',
      10,
    );
    const currency = /^[A-Z]{3}$/.test(root.dataset.cscCurrency || '')
      ? root.dataset.cscCurrency
      : 'USD';

    if (
      !form
      || !(input instanceof HTMLInputElement)
      || !(clearButton instanceof HTMLButtonElement)
      || !status
      || !loading
      || !results
      || !browse
      || !collectionUrl.startsWith('/collections/')
      || !Number.isInteger(collectionProductCount)
      || collectionProductCount < 0
    ) return;

    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    });
    let allProductsPromise = null;
    let searchGeneration = 0;

    const setBusy = (busy) => {
      root.setAttribute('aria-busy', String(busy));
      loading.hidden = !busy;
      form.querySelectorAll('input, button[type="submit"]').forEach((control) => {
        control.disabled = busy;
      });
    };

    const updateUrl = (query, shouldPush) => {
      const url = new URL(window.location.href);
      if (query) url.searchParams.set(QUERY_PARAMETER, query);
      else url.searchParams.delete(QUERY_PARAMETER);
      if (shouldPush) history.pushState({}, '', url);
      else history.replaceState({}, '', url);
    };

    const fetchPage = async (page) => {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const url = new URL(`${collectionUrl}/products.json`, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
        url.searchParams.set('limit', String(PAGE_SIZE));
        url.searchParams.set('page', String(page));
        const response = await window.fetch(url, {
          credentials: 'same-origin',
          headers: {Accept: 'application/json'},
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        const payload = await response.json();
        if (!Array.isArray(payload.products)) throw new Error('Invalid collection response');
        return payload.products.slice(0, PAGE_SIZE).map(selectProductRecord);
      } finally {
        window.clearTimeout(timeout);
      }
    };

    const fetchAllProducts = async () => {
      if (allProductsPromise) return allProductsPromise;
      allProductsPromise = (async () => {
        const requestedPageCount = Math.ceil(collectionProductCount / PAGE_SIZE);
        const pageCount = Math.min(MAX_PAGES, requestedPageCount);
        if (requestedPageCount > MAX_PAGES) {
          throw new Error('Collection exceeds the bounded search size');
        }
        if (pageCount === 0) return [];

        const pages = new Array(pageCount);
        let nextPageIndex = 0;
        const worker = async () => {
          while (nextPageIndex < pageCount) {
            const pageIndex = nextPageIndex;
            nextPageIndex += 1;
            pages[pageIndex] = await fetchPage(pageIndex + 1);
          }
        };
        const workerCount = Math.min(MAX_CONCURRENT_REQUESTS, pageCount);
        await Promise.all(Array.from({length: workerCount}, worker));

        const deduplicated = new Map();
        pages.flat().forEach((product) => {
          const id = String(product?.id || '');
          if (id && !deduplicated.has(id)) deduplicated.set(id, product);
        });
        return Array.from(deduplicated.values());
      })().catch((error) => {
        allProductsPromise = null;
        throw error;
      });
      return allProductsPromise;
    };

    const safeImageUrl = (rawUrl) => {
      if (!rawUrl) return '';
      try {
        const url = new URL(String(rawUrl), window.location.origin);
        return url.protocol === 'https:' ? url.href : '';
      } catch (_error) {
        return '';
      }
    };

    const priceText = (product) => {
      const minimum = product?.minimumPrice;
      const maximum = product?.maximumPrice;
      if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) return 'View product';
      return minimum < maximum ? `From ${money.format(minimum)}` : money.format(minimum);
    };

    const renderProductCard = (product) => {
      const title = typeof product?.title === 'string' ? product.title.trim() : '';
      const handle = typeof product?.handle === 'string' ? product.handle.trim() : '';
      if (!title || !/^[a-z0-9][a-z0-9_-]*$/i.test(handle)) return null;

      const productUrl = `${collectionUrl}/products/${encodeURIComponent(handle)}`;
      const article = document.createElement('article');
      article.className = 'csc-collection-card';

      const imageLink = document.createElement('a');
      imageLink.className = 'csc-collection-card-image';
      imageLink.href = productUrl;
      const imageRecord = product.image;
      const imageUrl = safeImageUrl(imageRecord?.src);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = title;
        image.loading = 'lazy';
        image.decoding = 'async';
        const imageWidth = Number.parseInt(imageRecord?.width, 10);
        const imageHeight = Number.parseInt(imageRecord?.height, 10);
        if (imageWidth > 0 && imageWidth <= 10000) image.width = imageWidth;
        if (imageHeight > 0 && imageHeight <= 10000) image.height = imageHeight;
        imageLink.append(image);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'csc-collection-placeholder';
        placeholder.textContent = 'No image available';
        imageLink.append(placeholder);
      }

      if (product.hasVariants && !product.available) {
        const soldOut = document.createElement('span');
        soldOut.className = 'csc-collection-soldout';
        soldOut.textContent = 'Sold Out';
        imageLink.append(soldOut);
      }

      const info = document.createElement('div');
      info.className = 'csc-collection-card-info';
      const titleLink = document.createElement('a');
      titleLink.className = 'csc-collection-product-title';
      titleLink.href = productUrl;
      titleLink.textContent = title;
      const price = document.createElement('div');
      price.className = 'csc-collection-price';
      price.textContent = priceText(product);
      info.append(titleLink, price);
      article.append(imageLink, info);
      return article;
    };

    const showBrowse = () => {
      results.replaceChildren();
      results.hidden = true;
      browse.hidden = false;
    };

    const applyQuery = async (rawQuery) => {
      const query = String(rawQuery || '').replace(/\s+/g, ' ').trim().slice(0, 80);
      const generation = searchGeneration + 1;
      searchGeneration = generation;
      input.value = query;
      clearButton.hidden = !query;

      if (query.length < 2) {
        showBrowse();
        status.textContent = `Browse ${collectionProductCount} products in this collection below.`;
        return;
      }

      setBusy(true);
      browse.hidden = true;
      results.hidden = true;
      status.textContent = `Searching all ${collectionProductCount} products…`;
      try {
        const products = await fetchAllProducts();
        if (generation !== searchGeneration) return;
        const normalizedQuery = normalize(query);
        const matches = products.filter((product) => normalize(product?.title).includes(normalizedQuery));
        const visibleMatches = matches.slice(0, MAX_RENDERED_RESULTS);
        const cards = visibleMatches.map(renderProductCard).filter(Boolean);
        results.replaceChildren(...cards);
        results.hidden = cards.length === 0;
        if (matches.length > MAX_RENDERED_RESULTS) {
          status.textContent = `Showing the first ${MAX_RENDERED_RESULTS} of ${matches.length} matching products. Add more words to narrow the search.`;
        } else if (matches.length) {
          status.textContent = `Showing ${matches.length} matching ${matches.length === 1 ? 'product' : 'products'} from the full collection.`;
        } else {
          status.textContent = `No products in this collection match “${query}”.`;
        }
      } catch (_error) {
        if (generation !== searchGeneration) return;
        showBrowse();
        status.textContent = 'Collection search could not load. Browse the products below or try again.';
      } finally {
        if (generation === searchGeneration) setBusy(false);
      }
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const query = input.value.replace(/\s+/g, ' ').trim();
      updateUrl(query, true);
      void applyQuery(query);
    });

    clearButton.addEventListener('click', () => {
      searchGeneration += 1;
      setBusy(false);
      updateUrl('', true);
      void applyQuery('');
      input.focus();
    });

    window.addEventListener('popstate', () => {
      const query = new URLSearchParams(window.location.search).get(QUERY_PARAMETER) || '';
      void applyQuery(query);
    });

    const initialQuery = new URLSearchParams(window.location.search)
      .get(QUERY_PARAMETER) || '';
    updateUrl(initialQuery, false);
    void applyQuery(initialQuery);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), {once: true});
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
